import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '@luacentrix/icons';
import { Button, IconButton } from './primitives';
import { Checkbox } from './selection';

export type SortDirection = 'asc' | 'desc';

export interface Column<Row> {
  id: string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'right';
  accessor: (row: Row) => ReactNode;
  sortValue?: (row: Row) => string | number;
}

export interface DataTableProps<Row> {
  columns: Array<Column<Row>>;
  rows: Row[];
  rowKey: (row: Row) => string;
  emptyTitle?: string;
  emptyDescription?: string;
  loading?: boolean;
  selection?: {
    selectedIds: string[];
    onChange: (ids: string[]) => void;
  };
  pageSize?: number;
  onRowClick?: (row: Row) => void;
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  loading = false,
  selection,
  pageSize = 10,
  onRowClick,
}: DataTableProps<Row>) {
  const [sort, setSort] = useState<{ id: string; direction: SortDirection } | null>(null);
  const [page, setPage] = useState(0);

  const ordered = useMemo(() => {
    if (sort === null) return rows;
    const column = columns.find(item => item.id === sort.id);
    if (column?.sortValue === undefined) return rows;
    const read = column.sortValue;
    return [...rows].sort((left, right) => {
      const a = read(left);
      const b = read(right);
      if (a === b) return 0;
      const result = a < b ? -1 : 1;
      return sort.direction === 'asc' ? result : -result;
    });
  }, [rows, columns, sort]);

  const pageCount = Math.max(1, Math.ceil(ordered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const window = ordered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const ids = window.map(rowKey);
  const allSelected =
    selection !== undefined &&
    ids.length > 0 &&
    ids.every(id => selection.selectedIds.includes(id));
  const someSelected =
    selection !== undefined && ids.some(id => selection.selectedIds.includes(id)) && !allSelected;

  if (loading) {
    return (
      <div className="lc-table-wrap" aria-busy="true">
        <div className="lc-stack" style={{ padding: 'var(--lc-space-7)' }}>
          {[0, 1, 2, 3].map(index => (
            <span key={index} className="lc-skeleton" style={{ height: 34 }} aria-hidden />
          ))}
        </div>
      </div>
    );
  }

  if (ordered.length === 0) {
    return (
      <div className="lc-table-wrap">
        <div className="lc-empty">
          <Icon name="database" size="xl" />
          <h3 className="lc-empty__title">{emptyTitle}</h3>
          {emptyDescription === undefined ? null : (
            <p className="lc-empty__description">{emptyDescription}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="lc-table-wrap">
        <table className="lc-table">
          <thead>
            <tr>
              {selection === undefined ? null : (
                <th style={{ width: 36 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    ariaLabel="Select all rows on this page"
                    onChange={next => {
                      if (next) {
                        selection.onChange(Array.from(new Set([...selection.selectedIds, ...ids])));
                      } else {
                        selection.onChange(selection.selectedIds.filter(id => !ids.includes(id)));
                      }
                    }}
                  />
                </th>
              )}
              {columns.map(column => {
                const active = sort?.id === column.id;
                return (
                  <th key={column.id} style={{ width: column.width, textAlign: column.align }}>
                    {column.sortable === true ? (
                      <button
                        type="button"
                        className="lc-table__sort"
                        aria-sort={
                          active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : 'none'
                        }
                        onClick={() =>
                          setSort(current => {
                            if (current?.id !== column.id)
                              return { id: column.id, direction: 'asc' };
                            if (current.direction === 'asc')
                              return { id: column.id, direction: 'desc' };
                            return null;
                          })
                        }
                      >
                        {column.header}
                        <Icon
                          name={
                            active
                              ? sort?.direction === 'asc'
                                ? 'chevronUp'
                                : 'chevronDown'
                              : 'sort'
                          }
                          size="xs"
                        />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {window.map(row => {
              const key = rowKey(row);
              const isSelected = selection?.selectedIds.includes(key) ?? false;
              return (
                <tr
                  key={key}
                  onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                  style={onRowClick === undefined ? undefined : { cursor: 'pointer' }}
                  data-selected={isSelected ? 'true' : undefined}
                >
                  {selection === undefined ? null : (
                    <td>
                      <Checkbox
                        checked={isSelected}
                        ariaLabel={`Select row ${key}`}
                        onChange={next =>
                          selection.onChange(
                            next
                              ? Array.from(new Set([...selection.selectedIds, key]))
                              : selection.selectedIds.filter(id => id !== key),
                          )
                        }
                      />
                    </td>
                  )}
                  {columns.map(column => (
                    <td key={column.id} style={{ textAlign: column.align }}>
                      {column.accessor(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pageCount > 1 ? (
        <div className="lc-pagination">
          <span>
            Page {safePage + 1} of {pageCount}
          </span>
          <span className="lc-row">
            <IconButton
              name="chevronLeft"
              label="Previous page"
              size="sm"
              disabled={safePage === 0}
              onClick={() => setPage(current => Math.max(0, current - 1))}
            />
            <IconButton
              name="chevronRight"
              label="Next page"
              size="sm"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage(current => Math.min(pageCount - 1, current + 1))}
            />
          </span>
        </div>
      ) : null}
    </>
  );
}

export function CodeBlock({
  code,
  language = 'text',
  showCopy = true,
}: {
  code: string;
  language?: string;
  showCopy?: boolean;
}) {
  const id = useId();
  return (
    <div className="lc-code">
      {showCopy ? (
        <span style={{ position: 'absolute', top: 8, right: 8 }}>
          <CopyButton value={code} label={`Copy ${language} snippet`} />
        </span>
      ) : null}
      <pre style={{ margin: 0 }} aria-label={`${language} code sample`}>
        <code id={id}>{code}</code>
      </pre>
    </div>
  );
}

export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
    },
    [],
  );

  return (
    <IconButton
      name={copied ? 'check' : 'copy'}
      label={copied ? 'Copied' : label}
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setCopied(false), 1600);
        });
      }}
    />
  );
}

export interface AccordionItemData {
  id: string;
  title: string;
  content: ReactNode;
}

export function Accordion({
  items,
  defaultOpen,
}: {
  items: AccordionItemData[];
  defaultOpen?: string;
}) {
  const [open, setOpen] = useState<string | null>(defaultOpen ?? null);
  const baseId = useId();
  return (
    <div className="lc-accordion">
      {items.map(item => {
        const expanded = open === item.id;
        return (
          <div key={item.id} className="lc-accordion__item">
            <button
              type="button"
              className="lc-accordion__trigger"
              id={`${baseId}-trigger-${item.id}`}
              aria-expanded={expanded}
              aria-controls={`${baseId}-panel-${item.id}`}
              onClick={() => setOpen(expanded ? null : item.id)}
            >
              {item.title}
              <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size="sm" />
            </button>
            {expanded ? (
              <div
                className="lc-accordion__panel"
                id={`${baseId}-panel-${item.id}`}
                role="region"
                aria-labelledby={`${baseId}-trigger-${item.id}`}
              >
                {item.content}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function Timeline({
  events,
}: {
  events: Array<{ id: string; title: string; meta: string; icon?: IconName }>;
}) {
  return (
    <ol className="lc-timeline" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {events.map(event => (
        <li key={event.id} className="lc-timeline__item">
          <p style={{ margin: 0, fontSize: 'var(--lc-size-sm)' }}>
            {event.icon === undefined ? null : <Icon name={event.icon} size="xs" />} {event.title}
          </p>
          <p className="lc-timeline__meta" style={{ margin: '2px 0 0' }}>
            {event.meta}
          </p>
        </li>
      ))}
    </ol>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="lc-page-header">
      <div className="lc-row lc-row--between" style={{ alignItems: 'flex-start' }}>
        <div className="lc-stack" style={{ gap: 'var(--lc-space-2)' }}>
          <h1 className="lc-page-header__title">{title}</h1>
          {description === undefined ? null : (
            <p className="lc-page-header__description">{description}</p>
          )}
        </div>
        {actions === undefined ? null : <div className="lc-action-bar">{actions}</div>}
      </div>
    </header>
  );
}

export function SectionHeader({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="lc-section-header">
      <h2 className="lc-section-header__title">{title}</h2>
      {action ?? null}
    </div>
  );
}

export function Breadcrumbs({ trail }: { trail: Array<{ label: string; href?: string }> }) {
  return (
    <nav aria-label="Breadcrumb">
      <ol className="lc-breadcrumbs">
        {trail.map((crumb, index) => (
          <li key={crumb.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {index > 0 ? <Icon name="chevronRight" size="xs" /> : null}
            {crumb.href === undefined ? (
              <span aria-current="page">{crumb.label}</span>
            ) : (
              <a href={crumb.href}>{crumb.label}</a>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  summary,
}: {
  page: number;
  pageCount: number;
  onPageChange: (next: number) => void;
  summary?: string;
}) {
  return (
    <div className="lc-pagination">
      <span>{summary ?? `Page ${page + 1} of ${pageCount}`}</span>
      <span className="lc-row">
        <Button
          size="sm"
          variant="ghost"
          icon="arrowLeft"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="ghost"
          iconRight="arrowRight"
          disabled={page >= pageCount - 1}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </span>
    </div>
  );
}
